import React, { useState } from 'react';
import { Box, Dialog, Icon, IconButton, Tab, Tabs } from '@mui/material';
import { Button } from 'global/components/Button';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Confirm from 'global/components/confirm';
import { FullScreen } from 'global/components/modal';
import { Row } from 'global/components/row';
import { Text } from 'global/components/text';

export const ASSIGNMENT_FORM_TAB = {
  location: 0,
  criteria: 1,
};

const ShellTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const PreviewDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '90vw',
    height: '90vh',
    maxWidth: 800,
    minWidth: 320,
    [theme.breakpoints.down('md')]: {
      width: '95vw',
      height: '95vh',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%',
      height: '100%',
    },
  },
}));

const PreviewDialogContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

/** Section wrapper used by tab headings + bodies (matches former styles.section in realm/room-assignments). */
export const AssignmentSection = styled('section')(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

/** Inset wrapper used inside tab panels (matches former styles.marginLeft in realm/room-assignments). */
export const AssignmentMarginLeft = styled('div')(({ theme }) => ({
  marginLeft: theme.spacing(3),
}));

export function AssignmentFormTabs({
  title,
  subtitleAdd,
  subtitle,
  value,
  onChange,
  className,
}) {
  return (
    <>
      <AssignmentSection>
        <Text type="headline_large" bold>
          {title}
        </Text>
        <Text type="label_large">
          {subtitleAdd ? <b>{subtitleAdd}</b> : null}
          {subtitle}
        </Text>
      </AssignmentSection>
      {className ? (
        <Tabs
          value={value}
          onChange={(_, v) => onChange(v)}
          className={className}
          indicatorColor="secondary"
          textColor="secondary"
        >
          <Tab label="Location" />
          <Tab label="Criteria" />
        </Tabs>
      ) : (
        <ShellTabs
          value={value}
          onChange={(_, v) => onChange(v)}
          indicatorColor="secondary"
          textColor="secondary"
        >
          <Tab label="Location" />
          <Tab label="Criteria" />
        </ShellTabs>
      )}
    </>
  );
}

/**
 * Single tab body: inset block with title; optional italic description; then content.
 * Omit `description` for criteria-style tabs.
 */
export function AssignmentFormTabPanel({ title, description, children }) {
  return (
    <AssignmentSection>
      <AssignmentMarginLeft>
        <Text type="headline_large" bold sx={{ mt: '5px', mb: '10px' }}>
          {title}
        </Text>
        {description ? (
          <Text type="label_large" italic sx={{ mb: '10px' }}>
            {description}
          </Text>
        ) : null}
        {children}
      </AssignmentMarginLeft>
    </AssignmentSection>
  );
}

/** Fixed Cancel / Save row used by assignment modals. */
export function AssignmentFormFooter({ onCancel, onSave, saving, cancelButtonTitle, saveButtonTitle }) {
  return (
    <Row center style={{ position: 'fixed', bottom: 10, left: 0, right: 0 }}>
      <Button variant="text" onClick={onCancel} title={cancelButtonTitle}>
        <Icon>close</Icon>
        Cancel
      </Button>
      <Button
        variant="contained"
        color="secondary"
        disabled={saving}
        onClick={onSave}
        style={{ marginLeft: 10 }}
        title={saveButtonTitle}
      >
        <Icon>save</Icon>
        Save
      </Button>
    </Row>
  );
}

/**
 * Full-screen modal shell with unsaved-changes confirm — shared by room- and realm-assignment modals.
 */
export function AssignmentsModalFrame({ modalProps, children }) {
  const [state, setState] = useState({
    hasChange: false,
  });
  const [conf, setconf] = useState();

  const closeModal = () => {
    modalProps.onClose();
    setState((s) => ({ ...s, hasChange: false }));
  };

  const onClose = (saved) => {
    if (state.hasChange && !saved) {
      setconf({
        title: 'Unsaved changes',
        content: 'You have unsaved data, are you sure you want to close?',
        onOk: () => {
          setconf();
          closeModal();
        },
        onCancel: () => {
          setconf();
        },
      });
    } else {
      closeModal();
    }
  };

  const changeHandlers = {
    onChange: () => setState((s) => ({ ...s, hasChange: true })),
    onClose: onClose,
    onCancel: onClose,
  };

  return (
    <>
      <FullScreen {...modalProps} onClose={onClose}>
        {children(changeHandlers)}
      </FullScreen>
      <Confirm {...conf} />
    </>
  );
}

/** Criteria / document “edit access” dialog header + scroll body; callers supply list content as children. */
export function AssignmentPreviewDialogFrame({ open, onClose, title, filter, children }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <PreviewDialog
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      maxWidth={false}
    >
      <PreviewDialogContainer>
        <Row style={{ alignItems: 'center', marginBottom: 16, flexShrink: 0, gap: 8 }}>
          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
          <Text type="headline_small" bold style={{ flex: 1, minWidth: 0 }}>
            {title}
          </Text>
          {filter && (
            <IconButton
              color={!filter.active ? 'secondary' : undefined}
              title={filter.active ? filter.titleShowAll : filter.titleShowOnly}
              onClick={filter.onToggle}
            >
              <Icon>{filter.active ? 'visibility_off' : 'visibility'}</Icon>
            </IconButton>
          )}
        </Row>
        <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>{children}</Box>
      </PreviewDialogContainer>
    </PreviewDialog>
  );
}
